import { gate } from 'hub-http/gates';
import type { PersonalizedTokenStyles } from '../commands/InsertPersonalizationToken';
import type { EditorConfig } from '../editorCapabilities/editorCapabilitiesTypes';
import { PERSONALIZATION_TOKEN } from '../editorCapabilities/constants';

export const PERSONALIZATION_TOKENS_P2 = gate(
  'CommPlatform:TeamSignature:ShowInCRM'
);

export function personalizationTokenStylesToString(
  tokenStyles: PersonalizedTokenStyles
) {
  return (
    Object.keys(tokenStyles)
      .map(
        (key) => `${key}: ${tokenStyles[key as keyof PersonalizedTokenStyles]}`
      )
      .join('; ') || undefined
  );
}

export function personalizationTokenStylesFromString(tokenStylesStr?: string) {
  const tokenStyles: PersonalizedTokenStyles = {};
  tokenStylesStr?.split(';').forEach((styleStr) => {
    const [__, key, value] =
      styleStr.match(
        /(margin|padding|fontColor|borderColor|backgroundColor|tokenIconKey): ([\w\-#()\s]+)/
      ) || [];
    if (key) tokenStyles[key as keyof PersonalizedTokenStyles] = value;
  });
  return tokenStyles;
}

export function formatPersonalizationToken(
  tokenKey: string,
  defaultTokenValue?: string
) {
  if (defaultTokenValue) {
    return `{{ ${tokenKey}|default('${defaultTokenValue}') }}`;
  }

  return `{{ ${tokenKey} }}`;
}

export function parseDefaultValueFromDOM(tokenKey: string | null, el: Element) {
  if (!tokenKey) return null;
  const tokenText = el.textContent || '';

  const defaultStart = tokenText.indexOf("|default('");
  if (defaultStart === -1) return null;

  const valueStart = defaultStart + "|default('".length;
  const valueEnd = tokenText.indexOf("')", valueStart);
  if (valueEnd === -1) return null;

  return tokenText.substring(valueStart, valueEnd);
}

export const formatDisplayText = (tokenKey: string) => {
  return tokenKey
    .replace(/\./g, ':')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export function getTokenTooltip(
  editorConfig: EditorConfig | undefined,
  tokenKey: string
) {
  const tokensConfig = editorConfig?.[PERSONALIZATION_TOKEN];

  return tokensConfig?.tokenTooltips?.[tokenKey] || null;
}

// The snippets render endpoint, when called with showMissingProperties=true,
// replaces each unresolvable token with a `{{ missing.<objectType>.<property> }}`
// marker at the token's original position (resolvable tokens render to their
// value text). This matches that marker.
const MISSING_TOKEN_MARKER = /\{\{\s*missing\.([^}]+?)\s*\}\}/g;

function escapeHtmlAttribute(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

// Rewrites the `{{ missing.* }}` markers in rendered snippet HTML into
// personalization_token span markup carrying error=true, so they parse back
// into highlighted error token chips (see ProsemirrorSchema parseDOM). Resolved
// content is left untouched. Returns the rewritten HTML plus the ordered list of
// missing token keys that were converted. Opt-in: only call this for the
// showMissingProperties render path; other consumers never see these markers.
export function convertMissingTokenMarkersToErrorTokenHtml(
  html: string,
  getErrorMessage?: (tokenKey: string) => string | undefined
): { html: string; tokenKeys: string[] } {
  const tokenKeys: string[] = [];
  const rewrittenHtml = html.replace(MISSING_TOKEN_MARKER, (_match, rawKey) => {
    const tokenKey = String(rawKey).trim();
    tokenKeys.push(tokenKey);
    const displayText = formatDisplayText(tokenKey);
    const errorMessage = getErrorMessage?.(tokenKey);
    const errorMessageAttr = errorMessage
      ? ` data-error-message="${escapeHtmlAttribute(errorMessage)}"`
      : '';
    return (
      `<span token-key="${escapeHtmlAttribute(tokenKey)}" ` +
      `display-text="${escapeHtmlAttribute(displayText)}" ` +
      `class="personalization-token" data-error="true"${errorMessageAttr}>` +
      `${formatPersonalizationToken(tokenKey)}</span>`
    );
  });
  return { html: rewrittenHtml, tokenKeys };
}
